import { CommonModule, DatePipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { forkJoin } from 'rxjs';
import { AsociacionesService } from '../../../api';
import {
  AlcancePermiso,
  AsociacionInstitucional,
  CargoPermisos,
  ConcesionPermiso,
  ETIQUETA_NIVEL,
  EntradaAuditoria,
  PermisoCatalogo
} from '../../core/permisos.model';
import { PermisosService } from '../../services/permisos.service';
import { AlertService } from '../alert/alert.service';
import { AlertButtonType } from '../dialog-alert/AlertButtonType.enum';
import { DialogAlertService } from '../dialog-alert/dialog-alert.service';
import { SpinnerComponent } from '../spinner/spinner.component';

export type EstadoPermiso = 'ninguno' | 'asociacion' | 'global';

/**
 * Configuración de permisos por cargo (P1.3-104), con el mismo planteamiento que la pantalla de permisos de
 * Secretaría Virtual (cargos desplegables, permisos agrupados por módulo) pero sobre el modelo de Censo:
 * concesiones por cargo con alcance global o de asociación, vigencia por ejercicio y asociaciones institucionales.
 * Los cambios se preparan como borrador, se confirman cuando son sensibles y los valida y audita el servidor.
 */
import { PageHeaderComponent } from '../ui/page-header.component';

@Component({
  selector: 'app-permisos',
  standalone: true,
  imports: [CommonModule, FormsModule, SpinnerComponent, PageHeaderComponent],
  providers: [DatePipe],
  templateUrl: './permisos.component.html',
  styleUrl: './permisos.component.scss'
})
export class PermisosComponent implements OnInit {
  readonly etiquetaNivel = ETIQUETA_NIVEL;

  catalogo: PermisoCatalogo[] = [];
  cargos: CargoPermisos[] = [];
  institucionales: AsociacionInstitucional[] = [];
  asociacionesDisponibles: { id: number; nombre: string }[] = [];
  administradoresVigentes: number | null = null;
  auditoria: EntradaAuditoria[] = [];

  expandidos = new Set<number>();
  borradores = new Map<number, Map<string, EstadoPermiso>>();
  nuevaInstitucional: number | null = null;
  asociacionSeleccionada: number | null = null;

  cargando = true;
  guardando = false;
  auditoriaAbierta = false;
  error = '';

  constructor(
    private readonly permisos: PermisosService,
    private readonly asociaciones: AsociacionesService,
    private readonly dialogos: DialogAlertService,
    private readonly alertas: AlertService,
    private readonly fecha: DatePipe
  ) {}

  ngOnInit(): void {
    this.cargar();
  }

  cargar(): void {
    this.cargando = true;
    this.error = '';
    forkJoin({
      catalogo: this.permisos.catalogo(),
      base: this.permisos.cargos(),
      asociaciones: this.asociaciones.asociacionesGet()
    }).subscribe({
      next: ({ catalogo, base, asociaciones }) => {
        this.catalogo = catalogo;
        this.borradores.clear();
        this.institucionales = base.asociacionesInstitucionales;
        this.administradoresVigentes = base.administradoresVigentes;
        this.asociacionesDisponibles = (asociaciones.asociaciones ?? [])
          .map(item => ({ id: Number(item.id), nombre: String(item.nombre ?? '') }))
          .sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'));
        // La asociación institucional con la que se trabaja es siempre un registro real del entorno.
        if (!this.institucionales.some(item => item.id === this.asociacionSeleccionada)) {
          this.asociacionSeleccionada = this.institucionales[0]?.id ?? null;
        }
        if (this.asociacionSeleccionada) {
          this.cargarCargos(this.asociacionSeleccionada);
        } else {
          this.cargos = base.cargos;
          this.cargando = false;
        }
      },
      error: (error: HttpErrorResponse) => {
        this.error = this.mensaje(error, 'No se han podido cargar los permisos.');
        this.cargando = false;
      }
    });
  }

  private cargarCargos(asociacionId: number): void {
    this.permisos.cargos(asociacionId).subscribe({
      next: respuesta => {
        this.cargos = respuesta.cargos;
        this.borradores.clear();
        this.administradoresVigentes = respuesta.administradoresVigentes;
        this.cargando = false;
      },
      error: (error: HttpErrorResponse) => {
        this.error = this.mensaje(error, 'No se han podido cargar los cargos.');
        this.cargando = false;
      }
    });
  }

  /** Cambia la asociación institucional con la que se trabaja (no se permite con cambios sin guardar). */
  cambiarAsociacion(id: number | null): void {
    if (this.borradores.size > 0) {
      this.alertas.warning('Guarda o descarta los cambios pendientes antes de cambiar de asociación.', 5000);
      return;
    }
    this.asociacionSeleccionada = id ? Number(id) : null;
    if (this.asociacionSeleccionada) {
      this.cargando = true;
      this.cargarCargos(this.asociacionSeleccionada);
    }
  }

  nombreAsociacionSeleccionada(): string {
    return this.institucionales.find(item => item.id === this.asociacionSeleccionada)?.nombre ?? '';
  }

  // --- Catálogo agrupado por módulo ---------------------------------------------------------------------------------

  porModulo(): { modulo: string; permisos: PermisoCatalogo[] }[] {
    const grupos = new Map<string, PermisoCatalogo[]>();
    for (const permiso of this.catalogo) {
      grupos.set(permiso.modulo, [...(grupos.get(permiso.modulo) ?? []), permiso]);
    }
    return [...grupos.entries()].map(([modulo, permisos]) => ({ modulo, permisos }));
  }

  admiteAsociacion(permiso: PermisoCatalogo): boolean {
    return permiso.alcances.includes('asociacion');
  }

  // --- Estado por cargo (borrador sobre lo guardado) ------------------------------------------------------------------

  private estadoGuardado(cargo: CargoPermisos, codigo: string): EstadoPermiso {
    const concesiones = cargo.permisos.filter(item => item.codigo === codigo);
    if (concesiones.some(item => item.alcance === 'global')) return 'global';
    if (concesiones.some(item => item.alcance === 'asociacion')) return 'asociacion';
    return 'ninguno';
  }

  estado(cargo: CargoPermisos, codigo: string): EstadoPermiso {
    return this.borradores.get(cargo.id)?.get(codigo) ?? this.estadoGuardado(cargo, codigo);
  }

  cambiar(cargo: CargoPermisos, codigo: string, nuevo: EstadoPermiso): void {
    const borrador = this.borradores.get(cargo.id) ?? new Map<string, EstadoPermiso>();
    if (nuevo === this.estadoGuardado(cargo, codigo)) {
      borrador.delete(codigo);
    } else {
      borrador.set(codigo, nuevo);
    }
    if (borrador.size) {
      this.borradores.set(cargo.id, borrador);
    } else {
      this.borradores.delete(cargo.id);
    }
  }

  modificado(cargo: CargoPermisos): boolean {
    return (this.borradores.get(cargo.id)?.size ?? 0) > 0;
  }

  descartar(cargo: CargoPermisos): void {
    this.borradores.delete(cargo.id);
  }

  totalConcedidos(cargo: CargoPermisos): number {
    return this.catalogo.filter(permiso => this.estado(cargo, permiso.codigo) !== 'ninguno').length;
  }

  alternar(id: number): void {
    if (this.expandidos.has(id)) {
      this.expandidos.delete(id);
    } else {
      this.expandidos.add(id);
    }
  }

  textoUltimoCambio(cargo: CargoPermisos): string {
    if (!cargo.ultimoCambio) return 'Sin cambios registrados';
    const cuando = this.fecha.transform(cargo.ultimoCambio.fecha, 'dd/MM/yyyy HH:mm') ?? '';
    const quien = cargo.ultimoCambio.responsable
      ?? (cargo.ultimoCambio.responsableId ? 'Persona #' + cargo.ultimoCambio.responsableId : 'Sistema');
    return `${cuando} · ${quien}`;
  }

  // --- Guardado con confirmación de cambios sensibles ---------------------------------------------------------------

  private concesionesDe(cargo: CargoPermisos): ConcesionPermiso[] {
    const resultado: ConcesionPermiso[] = [];
    for (const permiso of this.catalogo) {
      const estado = this.estado(cargo, permiso.codigo);
      if (estado === 'ninguno') continue;
      resultado.push({ codigo: permiso.codigo, alcance: estado as AlcancePermiso });
    }
    return resultado;
  }

  /** Resumen legible de lo que va a cambiar (para la confirmación). */
  resumenCambios(cargo: CargoPermisos): { anadidos: string[]; retirados: string[]; sensible: boolean } {
    const anadidos: string[] = [];
    const retirados: string[] = [];
    let sensible = false;
    const global = this.nombreAsociacionSeleccionada() ? `global en «${this.nombreAsociacionSeleccionada()}»` : 'global';
    const texto = (estado: EstadoPermiso) => estado === 'global' ? global : estado === 'asociacion' ? 'solo su asociación' : 'sin acceso';
    for (const permiso of this.catalogo) {
      const antes = this.estadoGuardado(cargo, permiso.codigo);
      const despues = this.estado(cargo, permiso.codigo);
      if (antes === despues) continue;
      const linea = `${permiso.codigo} (${texto(antes)} → ${texto(despues)})`;
      if (despues === 'ninguno' || (antes === 'global' && despues === 'asociacion')) {
        retirados.push(linea);
      } else {
        anadidos.push(linea);
      }
      if (permiso.sensible || permiso.codigo === 'permisos:gestionar') sensible = true;
    }
    return { anadidos, retirados, sensible };
  }

  guardar(cargo: CargoPermisos): void {
    if (this.guardando || !this.modificado(cargo)) return;
    const resumen = this.resumenCambios(cargo);
    if (!resumen.sensible) {
      this.enviar(cargo);
      return;
    }
    const titulares = cargo.titularesInstitucionales
      ? ` (${cargo.titularesInstitucionales} en asociaciones institucionales)`
      : '';
    const lineas = [
      `Vas a cambiar los permisos del cargo «${cargo.nombre}» en «${this.nombreAsociacionSeleccionada() || 'las asociaciones donde se ostenta'}».`,
      resumen.anadidos.length ? '\nSe conceden:\n• ' + resumen.anadidos.join('\n• ') : '',
      resumen.retirados.length ? '\nSe retiran:\n• ' + resumen.retirados.join('\n• ') : '',
      `\nAfecta a las personas que ostentan hoy ese cargo${titulares}. El cambio queda registrado con tu nombre y la fecha.`
    ].filter(Boolean).join('\n');
    this.dialogos.openDialogAlert({
      title: 'Confirmar cambio de permisos sensibles',
      content: lineas,
      buttonsAlert: [AlertButtonType.Cancelar, AlertButtonType.Aceptar]
    }).afterClosed().subscribe(respuesta => {
      if (respuesta === AlertButtonType.Aceptar) this.enviar(cargo);
    });
  }

  private enviar(cargo: CargoPermisos): void {
    this.guardando = true;
    this.permisos.actualizarCargo(cargo.id, this.concesionesDe(cargo), this.asociacionSeleccionada).subscribe({
      next: respuesta => {
        const actualizado = respuesta.cargos[0];
        if (actualizado) {
          this.cargos = this.cargos.map(item => item.id === actualizado.id ? actualizado : item);
        }
        this.borradores.delete(cargo.id);
        this.guardando = false;
        this.alertas.success(respuesta.cambios ? `Permisos de «${cargo.nombre}» actualizados.` : 'No había cambios que guardar.', 4000);
        this.refrescarAdministradores();
        if (this.auditoriaAbierta) this.cargarAuditoria();
      },
      error: (error: HttpErrorResponse) => {
        this.guardando = false;
        this.alertas.danger(this.mensaje(error, 'No se han podido guardar los permisos.'), 7000);
      }
    });
  }

  private refrescarAdministradores(): void {
    this.permisos.cargos().subscribe({
      next: respuesta => {
        this.administradoresVigentes = respuesta.administradoresVigentes;
      }
    });
  }

  // --- Asociaciones institucionales -----------------------------------------------------------------------------------

  disponiblesParaAnadir(): { id: number; nombre: string }[] {
    const actuales = new Set(this.institucionales.map(item => item.id));
    return this.asociacionesDisponibles.filter(item => !actuales.has(item.id));
  }

  anadirInstitucional(): void {
    if (!this.nuevaInstitucional) return;
    const objetivo = [...this.institucionales.map(item => item.id), Number(this.nuevaInstitucional)];
    this.confirmarInstitucionales(objetivo,
      'Una asociación institucional es la única donde un cargo puede conceder permisos globales de Censo. ' +
      'Añádela solo si representa a la Federación, no a una foguera o barraca.');
  }

  quitarInstitucional(asociacion: AsociacionInstitucional): void {
    const objetivo = this.institucionales.filter(item => item.id !== asociacion.id).map(item => item.id);
    this.confirmarInstitucionales(objetivo,
      `Quien ostente cargos en «${asociacion.nombre ?? asociacion.id}» dejará de recibir permisos globales de Censo.`);
  }

  private confirmarInstitucionales(ids: number[], aviso: string): void {
    this.dialogos.openDialogAlert({
      title: 'Confirmar cambio de asociaciones institucionales',
      content: aviso + '\n\nEl cambio queda registrado con tu nombre y la fecha.',
      buttonsAlert: [AlertButtonType.Cancelar, AlertButtonType.Aceptar]
    }).afterClosed().subscribe(respuesta => {
      if (respuesta !== AlertButtonType.Aceptar) return;
      this.permisos.actualizarInstitucionales(ids).subscribe({
        next: lista => {
          this.institucionales = lista;
          this.nuevaInstitucional = null;
          this.alertas.success('Asociaciones institucionales actualizadas.', 4000);
          this.refrescarAdministradores();
          this.cargar();
        },
        error: (error: HttpErrorResponse) => this.alertas.danger(this.mensaje(error, 'No se ha podido actualizar la lista.'), 7000)
      });
    });
  }

  // --- Auditoría -----------------------------------------------------------------------------------------------------

  alternarAuditoria(): void {
    this.auditoriaAbierta = !this.auditoriaAbierta;
    if (this.auditoriaAbierta) this.cargarAuditoria();
  }

  private cargarAuditoria(): void {
    this.permisos.auditoria(50).subscribe({
      next: lista => {
        this.auditoria = lista;
      },
      error: (error: HttpErrorResponse) => this.alertas.danger(this.mensaje(error, 'No se ha podido cargar el registro.'), 5000)
    });
  }

  private mensaje(error: HttpErrorResponse, defecto: string): string {
    return error?.error?.status?.message || error?.error?.message || defecto;
  }
}
